/**
 * CalendarSettings — everything about the calendar, in one place.
 *
 * CAL-SET-1. Rennan: *"also calendar neeeds a little settings button
 * and put all the information in that that can give it its settings."*
 *
 * WHAT WAS SCATTERED
 *
 * Week start was a `const` in the component with no UI at all. Paper
 * and accent were a panel down the left rail, between the calendar
 * list and nothing. Working hours and booking links were a collapsed
 * block BELOW the grid — PLANNER-4 put them there deliberately so a
 * studio could author a /book/:slug without leaving the calendar, and
 * that reasoning is still right; the position was the problem, not the
 * presence. Connected calendars were inside that, two levels down.
 *
 * So: one sheet, opened from one gear, holding all of it. The calendar
 * LIST stays in the rail — which calendars you are looking at is
 * something you toggle while looking, not a preference you set once.
 *
 * WHY A SHEET AND NOT A RAIL PANEL
 *
 * The availability editor alone is taller than the rail, and it is
 * mounted here rather than reimplemented — the same component Settings
 * → Scheduling renders, because a second booking-link editor built for
 * the calendar would be a second one to keep in step, and they would
 * not stay in step. A thing that tall belongs in something that
 * scrolls and then goes away.
 */
import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import SchedulingSettings from '@/components/settings/SchedulingSettings';
import { cn } from '@/lib/utils';

export type WeekStart = 0 | 1;

const WEEK_STARTS = [
  { id: 0 as WeekStart, name: 'Sunday' },
  { id: 1 as WeekStart, name: 'Monday' },
];

export function CalendarSettings<TView extends string, TPaper extends string, TAccent extends string>({
  open,
  onClose,
  views,
  view,
  onView,
  weekStart,
  onWeekStart,
  papers,
  paper,
  onPaper,
  accents,
  accent,
  onAccent,
}: {
  open: boolean;
  onClose: () => void;
  views: readonly { id: TView; name: string }[];
  view: TView;
  onView: (v: TView) => void;
  weekStart: WeekStart;
  onWeekStart: (v: WeekStart) => void;
  papers: readonly { id: TPaper; name: string }[];
  paper: TPaper;
  onPaper: (v: TPaper) => void;
  accents: readonly { id: TAccent; name: string; color: string }[];
  accent: TAccent;
  onAccent: (v: TAccent) => void;
}) {
  // Escape closes, and the listener is on `window` rather than on the
  // sheet so it works wherever focus happens to be — including the
  // backdrop, which is not focusable.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Calendar settings"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'grid',
        placeItems: 'center',
        padding: 20,
        background: 'rgba(0,0,0,0.42)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        className="plan-panel"
        // The backdrop closes; the sheet must not, or every click in
        // it closes the thing being used.
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(720px, 100%)',
          maxHeight: '86vh',
          overflowY: 'auto',
          padding: 0,
          boxShadow: '0 32px 80px -24px rgba(0,0,0,0.5)',
        }}
      >
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '16px 18px',
            background: 'var(--panel)',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <strong style={{ fontSize: 15 }}>Calendar settings</strong>
          <button type="button" className="plan-btn plan-icon" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 22 }}>
          <Group
            title="Week starts on"
            hint="The month grid, the week view and the week's date range all follow this."
          >
            <Segmented
              options={WEEK_STARTS}
              value={weekStart}
              onChange={onWeekStart}
              idOf={(o) => o.id}
            />
          </Group>

          <Group title="Opens on" hint="Which view the calendar comes up in.">
            <Segmented options={views} value={view} onChange={onView} idOf={(o) => o.id} />
          </Group>

          <Group
            title="Appearance"
            hint="Light and Dark set Orbit's own mode, so the rest of the app follows. Sand is a paper over light."
          >
            <Segmented options={papers} value={paper} onChange={onPaper} idOf={(o) => o.id} />
            <div style={{ display: 'flex', gap: 10, paddingTop: 12 }}>
              {accents.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={cn('plan-sw', accent === a.id && 'on')}
                  style={{ background: a.color }}
                  aria-label={a.name}
                  aria-pressed={accent === a.id}
                  onClick={() => onAccent(a.id)}
                />
              ))}
            </div>
          </Group>

          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 18 }}>
            {/* Working hours, booking links and the connected
                calendars — the same panel Settings → Scheduling
                renders, mounted rather than rebuilt. */}
            <SchedulingSettings />
          </div>
        </div>
      </div>
    </div>
  );
}

function Group({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
      {hint && (
        <p style={{ margin: '3px 0 10px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
          {hint}
        </p>
      )}
      {children}
    </section>
  );
}

/** The artboard's segmented control, for any list of two or more. */
function Segmented<T, V>({
  options,
  value,
  onChange,
  idOf,
}: {
  options: readonly T[];
  value: V;
  onChange: (v: V) => void;
  idOf: (o: T) => V;
}) {
  return (
    <div style={{ display: 'flex', background: 'var(--track)', borderRadius: 10, padding: 2, gap: 2 }}>
      {options.map((o) => {
        const id = idOf(o);
        return (
          <button
            key={String(id)}
            type="button"
            className={cn('plan-seg', value === id && 'on')}
            style={{ flex: 1, padding: 0 }}
            aria-pressed={value === id}
            onClick={() => onChange(id)}
          >
            {(o as { name: string }).name}
          </button>
        );
      })}
    </div>
  );
}

export default CalendarSettings;
